'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useProductDescriptionData } from '@/screens/Product/hooks/use-product-description-data'
import { useProduct } from '@/screens/Product/hooks/useProduct'

import { ProductReviews, useProductCurrentReviewSummary, useProductReviewsTabData } from '@/features/product-reviews'
import { Pickup } from '@/features/product/choose-pickup-point'

import { Tabs } from '@/shared/ui'

import { DescriptionTab } from '../../ui/product-tabs/description-tab/description-tab'
import { TechSpecsTab } from '../../ui/product-tabs/tech-specs-tab/tech-specs-tab'

import { isDiscountProductBuy } from './is-discount-product-buy'

const TABS_ID_ATTR = `productTabs`
const VALID_TAB_KEYS = [`description`, `specs`, `reviews`, `map`]

export const DiscountProductTabs = (): JSX.Element => {
	const searchParams = useSearchParams()
	const { product } = useProduct()
	const isProductBuy = isDiscountProductBuy(product.ORDER_TYPE)
	const { data: description } = useProductDescriptionData(product.ID)

	const tabFromUrl = searchParams?.get(`tab`) ?? null
	const desiredTab = tabFromUrl && VALID_TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : null

	const [activeTab, setActiveTab] = useState(desiredTab || `specs`)
	const hasScrolledRef = useRef(false)
	const { summary: reviewsSummary } = useProductCurrentReviewSummary(product)
	const reviews = useProductReviewsTabData(product, [], activeTab === `reviews`, reviewsSummary)
	const reviewsCount = reviewsSummary?.COUNT ?? 0
	const reviewsTabTitle = reviewsCount > 0 ? `Отзывы (${reviewsCount})` : `Отзывы`

	const tabItems = useMemo(
		() => [
			{
				tab: `Описание`,
				content: <DescriptionTab description={description} productKod={product.KOD} />,
				key: `description`,
				hidden: !description,
				forceRender: true
			},
			{
				tab: `Характеристики`,
				content: <TechSpecsTab productTitle={product.TITLE} items={product.PROPERTIES} />,
				key: `specs`,
				forceRender: true
			},
			{
				tab: reviewsTabTitle,
				content: (
					<ProductReviews
						productCode={Number(product.KOD)}
						productCodes={reviews.productCodes}
						productTitle={product.TITLE}
						productImages={reviews.productImages}
						token={reviews.token}
						initialData={reviews.initialData}
						isLoading={reviews.isLoading}
						hasOwnCurrentVariantReview={reviews.hasOwnCurrentVariantReview}
					/>
				),
				key: `reviews`,
				forceRender: true
			},
			{
				tab: `Наличие в магазинах`,
				content: <Pickup inProduct discountProductId={product.ID} />,
				key: `map`,
				hidden: !isProductBuy,
				forceRender: true
			}
		],
		[description, isProductBuy, product.ID, product.KOD, product.PROPERTIES, product.TITLE, reviews, reviewsTabTitle]
	)

	useEffect(() => {
		if (!desiredTab || hasScrolledRef.current) return

		const targetTab = tabItems.find(item => item.key === desiredTab && !item.hidden)
		if (!targetTab) return

		setActiveTab(desiredTab)

		const timeoutId = setTimeout(() => {
			const tabsEl = document.getElementById(TABS_ID_ATTR)
			if (tabsEl && !hasScrolledRef.current) {
				hasScrolledRef.current = true
				const elementTop = tabsEl.getBoundingClientRect().top + window.scrollY
				const offset = window.innerWidth <= 848 ? 70 : 160
				window.scrollTo({ top: elementTop - offset, behavior: 'smooth' })
			}
		}, 300)

		return () => clearTimeout(timeoutId)
	}, [desiredTab, tabItems])

	return (
		<Tabs id={TABS_ID_ATTR} defaultActiveKey="specs" activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
	)
}
